import { useEffect, useMemo, useRef, useState } from "react";
import { AlertCircle, ClipboardList, LifeBuoy, RefreshCw, Search, ShieldCheck, X } from "lucide-react";
import { getAdminUsers, getKycCases, openKycEscalation, recheckKyc, updateKycCase, updateKycEscalation } from "../lib/api";
import { startGoogleSession } from "../lib/auth";
import { isKycCaseOpen, isKycSlaRisk, kycAgeDays, kycStage, kycStageInfo, KYC_STAGES, type KycStage } from "../lib/kycOps";
import {
  buildEscalationPacket, canEscalate, escalationKindInfo, escalationSlaLabel, escalationSlaState,
  escalationStatusLabel, isEscalationLive, suggestedEscalationKind
} from "../lib/kycEscalation";
import { formatPersonName } from "../lib/directory";
import type { AdminUserSummary, KycCase, KycEscalation, KycEscalationKind, Viewer } from "../types";
import { KycInvestigationPanel } from "./KycInvestigationPanel";
import { PageHeading } from "./PageHeading";
import { SkeletonRows } from "./Skeleton";
import { TableEmpty } from "./TableEmpty";
import { FormRequirements } from "./FormRequirements";
import { DEPLOYMENT_NAME } from "../lib/runtime";

interface KycPageProps { viewer: Viewer; onOpenCustomer: (user: AdminUserSummary) => void; onNotifyCustomer: (user: AdminUserSummary) => void; }

/** Chips narrow the queue by who owns a case, not by what the provider thinks of it. */
type OwnerChip = "mine" | "unassigned" | "overdue" | "urgent" | "withProvider" | "slaBreached";
const OWNER_CHIPS: Array<{ key: OwnerChip; label: string }> = [
  { key: "overdue", label: "Over 48h" },
  { key: "urgent", label: "Urgent" },
  { key: "unassigned", label: "Unassigned" },
  { key: "mine", label: "Assigned to me" },
  { key: "withProvider", label: "With provider" },
  // A breached provider SLA is the one signal worth a Slack nudge, so it gets its own
  // sweep rather than hiding inside the general overdue count.
  { key: "slaBreached", label: "Provider SLA breached" }
];

const ESCALATION_KINDS: KycEscalationKind[] = ["reinstate", "unfreeze", "stalled_review", "duplicate"];

export function KycPage({ viewer, onOpenCustomer, onNotifyCustomer }: KycPageProps) {
  const [users, setUsers] = useState<AdminUserSummary[]>([]);
  const [cases, setCases] = useState<KycCase[]>([]);
  const [stageFilter, setStageFilter] = useState<KycStage | null>(null);
  const [chips, setChips] = useState<Set<OwnerChip>>(new Set());
  const [query, setQuery] = useState("");
  const [workflowFilter, setWorkflowFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<AdminUserSummary | null>(null);
  const [detailToken, setDetailToken] = useState(0);
  const [reason, setReason] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const dialogRef = useRef<HTMLDialogElement>(null);
  const caseDialogRef = useRef<HTMLDialogElement>(null);
  const [workflowStatus, setWorkflowStatus] = useState<KycCase["workflowStatus"]>("unassigned");
  const [priority, setPriority] = useState<KycCase["priority"]>("normal");
  const [assigneeEmail, setAssigneeEmail] = useState("");
  const [caseNote, setCaseNote] = useState("");
  const [caseReason, setCaseReason] = useState("");
  const [caseConfirmation, setCaseConfirmation] = useState("");
  const escalateDialogRef = useRef<HTMLDialogElement>(null);
  const escalationUpdateDialogRef = useRef<HTMLDialogElement>(null);
  const [escalationKind, setEscalationKind] = useState<KycEscalationKind>("reinstate");
  const [escalationContext, setEscalationContext] = useState("");
  const [canonicalGuid, setCanonicalGuid] = useState("");
  const [escalationReason, setEscalationReason] = useState("");
  const [escalationConfirmation, setEscalationConfirmation] = useState("");
  const [escalationStatus, setEscalationStatus] = useState<KycEscalation["status"]>("open");
  const [escalationReference, setEscalationReference] = useState("");
  const [escalationReferenceUrl, setEscalationReferenceUrl] = useState("");
  const [escalationOutcome, setEscalationOutcome] = useState("");
  const [escalationUpdateReason, setEscalationUpdateReason] = useState("");
  const [escalationUpdateConfirmation, setEscalationUpdateConfirmation] = useState("");

  const load = () => {
    setLoading(true);
    setError(null);
    void Promise.all([getAdminUsers(), getKycCases()])
      .then(([{ users: result }, caseResult]) => {
        setUsers(result);
        setCases(caseResult);
        setSelected((current) => result.find((user) => user.id === current?.id) ?? null);
      })
      .catch((reason) => setError(reason instanceof Error ? reason.message : "Unable to load KYC queue"))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const casesByCustomer = useMemo(() => new Map(cases.map((item) => [item.customerGuid, item])), [cases]);
  const caseFor = (user: AdminUserSummary) => user.customerGuid ? casesByCustomer.get(user.customerGuid) : undefined;

  const counts = useMemo(() => users.reduce((result, user) => {
    result[kycStage(user.state)] += 1;
    return result;
  }, { verified: 0, processing: 0, customerAction: 0, blocked: 0, unknown: 0 } as Record<KycStage, number>), [users]);

  const chipCounts = useMemo(() => users.reduce((result, user) => {
    const item = caseFor(user);
    const open = isKycCaseOpen(user, item);
    if (open && !item?.assigneeEmail) result.unassigned += 1;
    if (open && item?.priority === "urgent") result.urgent += 1;
    if (isKycSlaRisk(user, item)) result.overdue += 1;
    if (open && item?.assigneeEmail?.toLowerCase() === viewer.email.toLowerCase()) result.mine += 1;
    if (item?.escalation && isEscalationLive(item.escalation)) result.withProvider += 1;
    if (item?.escalation && escalationSlaState(item.escalation) === "breached") result.slaBreached += 1;
    return result;
  }, { mine: 0, unassigned: 0, overdue: 0, urgent: 0, withProvider: 0, slaBreached: 0 } as Record<OwnerChip, number>), [casesByCustomer, users, viewer.email]);

  const visible = useMemo(() => users.filter((user) => {
    const item = caseFor(user);
    if (stageFilter && kycStage(user.state) !== stageFilter) return false;
    if (workflowFilter && (item?.workflowStatus ?? "unassigned") !== workflowFilter) return false;
    const term = query.trim().toLowerCase();
    if (term) {
      const searchable = `${user.name ?? ""} ${user.emailMasked ?? ""} ${user.customerGuid ?? ""} ${user.firebaseUid ?? ""}`.toLowerCase();
      if (!searchable.includes(term)) return false;
    }
    // Chips are additive: every selected chip has to hold, so combining them narrows.
    const open = isKycCaseOpen(user, item);
    if (chips.has("overdue") && !isKycSlaRisk(user, item)) return false;
    if (chips.has("urgent") && !(open && item?.priority === "urgent")) return false;
    if (chips.has("unassigned") && !(open && !item?.assigneeEmail)) return false;
    if (chips.has("mine") && !(open && item?.assigneeEmail?.toLowerCase() === viewer.email.toLowerCase())) return false;
    if (chips.has("withProvider") && !(item?.escalation && isEscalationLive(item.escalation))) return false;
    if (chips.has("slaBreached") && !(item?.escalation && escalationSlaState(item.escalation) === "breached")) return false;
    return true;
  }), [casesByCustomer, chips, query, stageFilter, users, viewer.email, workflowFilter]);

  const toggleChip = (key: OwnerChip) => setChips((current) => {
    const next = new Set(current);
    if (!next.delete(key)) next.add(key);
    return next;
  });
  const filtersActive = Boolean(stageFilter || workflowFilter || query.trim() || chips.size);
  const clearFilters = () => { setStageFilter(null); setWorkflowFilter(""); setQuery(""); setChips(new Set()); };

  const canAct = viewer.role === "superAdmin";
  const openRetry = () => { setReason(""); setConfirmation(""); setError(null); dialogRef.current?.showModal(); };
  const retry = async () => {
    if (!selected?.customerGuid || !selected.firebaseUid) return;
    setSaving(true); setError(null); setNotice(null);
    try {
      await startGoogleSession();
      const result = await recheckKyc({ customerGuid: selected.customerGuid, firebaseUid: selected.firebaseUid, reason: reason.trim(), confirmation });
      setNotice(`${formatPersonName(selected.name) ?? "Customer"}: provider reports ${result.kycState ?? "unknown"} / ${result.identityVerificationState ?? "unknown"} — app status ${result.appStatus}.`);
      dialogRef.current?.close();
      setDetailToken((token) => token + 1);
      load();
    } catch (retryError) { setError(retryError instanceof Error ? retryError.message : "KYC status could not be refreshed"); }
    finally { setSaving(false); }
  };
  const openCase = () => {
    if (!selected) return;
    const existing = caseFor(selected);
    setWorkflowStatus(existing?.workflowStatus ?? "unassigned");
    setPriority(existing?.priority ?? "normal");
    setAssigneeEmail(existing?.assigneeEmail ?? "");
    setCaseNote(""); setCaseReason(""); setCaseConfirmation(""); setError(null);
    caseDialogRef.current?.showModal();
  };
  const saveCase = async () => {
    if (!selected?.customerGuid || !selected.firebaseUid) return;
    setSaving(true); setError(null); setNotice(null);
    try {
      await startGoogleSession();
      const result = await updateKycCase({
        customerGuid: selected.customerGuid, firebaseUid: selected.firebaseUid,
        workflowStatus, priority, assigneeEmail: assigneeEmail.trim(), note: caseNote.trim(),
        reason: caseReason.trim(), confirmation: caseConfirmation
      });
      setCases((current) => [...current.filter((item) => item.customerGuid !== result.customerGuid), result]);
      setNotice(`${formatPersonName(selected.name) ?? "Customer"} case updated to ${result.workflowStatus.replaceAll("_", " ")}.`);
      caseDialogRef.current?.close();
      setDetailToken((token) => token + 1);
    } catch (caseError) { setError(caseError instanceof Error ? caseError.message : "KYC case could not be updated"); }
    finally { setSaving(false); }
  };

  const selectedCase = selected ? caseFor(selected) : undefined;
  const selectedEscalation = selectedCase?.escalation ?? null;

  const openEscalate = () => {
    if (!selected) return;
    // Pre-select what the state actually justifies; the operator can still assert a
    // duplicate, which nothing in the record can tell us about.
    setEscalationKind(suggestedEscalationKind(selected.state, kycAgeDays(selected, selectedCase)) ?? "duplicate");
    setEscalationContext(""); setCanonicalGuid(""); setEscalationReason(""); setEscalationConfirmation(""); setError(null);
    escalateDialogRef.current?.showModal();
  };
  const submitEscalation = async () => {
    if (!selected?.customerGuid || !selected.firebaseUid) return;
    setSaving(true); setError(null); setNotice(null);
    try {
      await startGoogleSession();
      const result = await openKycEscalation({
        customerGuid: selected.customerGuid, firebaseUid: selected.firebaseUid, kind: escalationKind,
        context: escalationContext.trim(), canonicalCustomerGuid: canonicalGuid.trim(),
        reason: escalationReason.trim(), confirmation: escalationConfirmation
      });
      setCases((current) => {
        const existing = current.find((item) => item.customerGuid === result.customerGuid);
        const merged: KycCase = existing
          ? { ...existing, workflowStatus: "escalated", escalation: result.escalation }
          : {
            customerGuid: result.customerGuid, firebaseUid: selected.firebaseUid, workflowStatus: "escalated",
            priority: "normal", assigneeEmail: null, latestNote: null, noteCount: 0,
            updatedAt: result.escalation.updatedAt, updatedBy: result.escalation.updatedBy, escalation: result.escalation
          };
        return [...current.filter((item) => item.customerGuid !== result.customerGuid), merged];
      });
      setNotice(result.filingError
        ? `Escalation recorded, but it was not filed automatically (${result.filingError}). Copy the packet from the case panel, file it with the provider, and record the ticket reference.`
        : `Escalation filed with the provider as ticket ${result.escalation.reference ?? "unknown"}.`);
      escalateDialogRef.current?.close();
      setDetailToken((token) => token + 1);
    } catch (escalationError) { setError(escalationError instanceof Error ? escalationError.message : "The escalation could not be opened"); }
    finally { setSaving(false); }
  };

  const openEscalationUpdate = () => {
    if (!selectedEscalation) return;
    setEscalationStatus(selectedEscalation.status === "awaiting_reference" ? "open" : selectedEscalation.status);
    setEscalationReference(selectedEscalation.reference ?? "");
    setEscalationReferenceUrl(selectedEscalation.referenceUrl ?? "");
    setEscalationOutcome(selectedEscalation.outcomeNote ?? "");
    setEscalationUpdateReason(""); setEscalationUpdateConfirmation(""); setError(null);
    escalationUpdateDialogRef.current?.showModal();
  };
  const submitEscalationUpdate = async () => {
    if (!selected?.customerGuid) return;
    setSaving(true); setError(null); setNotice(null);
    try {
      await startGoogleSession();
      const result = await updateKycEscalation({
        customerGuid: selected.customerGuid, status: escalationStatus, reference: escalationReference.trim(),
        referenceUrl: escalationReferenceUrl.trim(), outcomeNote: escalationOutcome.trim(),
        reason: escalationUpdateReason.trim(), confirmation: escalationUpdateConfirmation
      });
      setCases((current) => current.map((item) => item.customerGuid === result.customerGuid ? { ...item, escalation: result.escalation } : item));
      setNotice(`Escalation for ${formatPersonName(selected.name) ?? "customer"} is now ${escalationStatusLabel(result.escalation.status).toLowerCase()}.`);
      escalationUpdateDialogRef.current?.close();
      setDetailToken((token) => token + 1);
    } catch (escalationError) { setError(escalationError instanceof Error ? escalationError.message : "The escalation could not be updated"); }
    finally { setSaving(false); }
  };

  const escalationPreview = selected ? buildEscalationPacket({
    kind: escalationKind,
    environment: DEPLOYMENT_NAME,
    customerGuid: selected.customerGuid,
    identityVerificationGuid: selected.identityVerificationGuid,
    personaInquiryId: selected.personaInquiryId,
    appStatus: selected.state,
    ageDays: kycAgeDays(selected, selectedCase),
    operatorNote: escalationContext,
    canonicalCustomerGuid: canonicalGuid
  }) : null;

  return (
    <div className="page-stack">
      <PageHeading
        eyebrow="IDENTITY OPERATIONS"
        title={<>KYC &amp; compliance</>}
        description={`Work the ${DEPLOYMENT_NAME} verification queue by who each case is waiting on.`}
        info={{
          label: "what this portal can and cannot do",
          children: <><strong>Provider truth, portal workflow.</strong> Cybrid and Persona own the verification decision; this portal cannot approve, overturn, or appeal one. What it can do is re-read the provider's answer, prompt the customer to restart, and track the case. Assignment, priority, and notes are portal-owned and separately audited.</>
        }}
        actions={<button className="secondary-button" onClick={load} disabled={loading}><RefreshCw size={16} className={loading ? "spin" : undefined} />Refresh</button>}
      />
      {notice ? <div className="success-panel">{notice}</div> : null}

      {/* One mutually exclusive row: each stage is a different next action, so a case
          belongs to exactly one of them and picking two would be incoherent. */}
      <div className="kyc-stage-grid">
        {KYC_STAGES.map((stage) => {
          const info = kycStageInfo(stage);
          const active = stageFilter === stage;
          return <button
            key={stage}
            className={`kyc-stage-card tone-${info.tone}${active ? " active" : ""}`}
            aria-pressed={active}
            onClick={() => setStageFilter(active ? null : stage)}
          >
            <span className="kyc-stage-count">{counts[stage]}</span>
            <strong>{info.label}</strong>
            <small>Waiting on {info.waitingOn.toLowerCase()}</small>
          </button>;
        })}
      </div>

      <div className="kyc-filter-bar">
        <Search size={17} aria-hidden="true" />
        <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search customer or identifier" aria-label="Search KYC queue" />
        <select value={workflowFilter} onChange={(event) => setWorkflowFilter(event.target.value)} aria-label="Filter workflow">
          <option value="">All workflows</option>
          <option value="unassigned">Unassigned</option>
          <option value="open">Open</option>
          <option value="in_review">In review</option>
          <option value="waiting_customer">Waiting for customer</option>
          <option value="escalated">Escalated</option>
          <option value="resolved">Resolved</option>
        </select>
        <button className="secondary-button" onClick={clearFilters} disabled={!filtersActive}>Clear</button>
      </div>
      <div className="kyc-chip-row">
        {OWNER_CHIPS.map(({ key, label }) => <button
          key={key}
          className={chips.has(key) ? "kyc-chip active" : "kyc-chip"}
          aria-pressed={chips.has(key)}
          onClick={() => toggleChip(key)}
        >{label}<span>{chipCounts[key]}</span></button>)}
      </div>

      {error ? <div className="error-panel"><AlertCircle size={19} /><div><strong>KYC queue unavailable</strong><p>{error}</p></div></div> : null}

      <div className="explorer-layout wide-detail">
        <section className="history-card explorer-table-card">
          <div className="section-heading-row"><h2>Verification queue</h2><span className="history-count">{loading && visible.length === 0 ? "Loading…" : `${visible.length} shown`}</span></div>
          <div className="table-wrap"><table aria-busy={loading}>
            <thead><tr><th>User</th><th>Stage</th><th>Case</th><th>Age</th><th>Owner</th></tr></thead>
            <tbody>
              {loading && visible.length === 0 ? <SkeletonRows columns={5} /> : null}
                {!loading && visible.length === 0 ? <TableEmpty columns={5} filtered={filtersActive} emptyMessage="No verification cases yet" onClear={clearFilters} /> : null}
              {visible.map((user) => {
                const item = caseFor(user);
                const age = kycAgeDays(user, item);
                const slaRisk = isKycSlaRisk(user, item);
                const info = kycStageInfo(kycStage(user.state));
                return <tr key={user.id} className={selected?.id === user.id ? "selected-row" : undefined} onClick={() => setSelected(user)}>
                  <td><strong className="cell-title">{formatPersonName(user.name) ?? "Unnamed user"}</strong><span className="cell-subtitle">{user.emailMasked || user.customerGuid || "—"}</span></td>
                  <td><span className={`status-badge stage-${info.tone}`}>{info.label}</span><span className="cell-subtitle">{user.state}</span></td>
                  <td>
                    <span className="case-state">{(item?.workflowStatus ?? "unassigned").replaceAll("_", " ")}</span>
                    {item?.escalation && isEscalationLive(item.escalation)
                      ? <span className={escalationSlaState(item.escalation) === "breached" ? "cell-subtitle sla-risk" : "cell-subtitle"}>{escalationSlaLabel(escalationSlaState(item.escalation))}</span>
                      : item?.noteCount ? <span className="cell-subtitle">{item.noteCount} note{item.noteCount === 1 ? "" : "s"}</span> : null}
                  </td>
                  <td><span className={slaRisk ? "sla-age sla-risk" : "sla-age"}>{age === null ? "—" : `${age}d`}</span></td>
                  <td><span className="cell-subtitle">{item?.assigneeEmail || "Unassigned"}</span></td>
                </tr>;
              })}
            </tbody>
          </table></div>
        </section>

        <aside className="detail-card">
          <KycInvestigationPanel
            user={selected}
            item={selectedCase}
            viewer={viewer}
            refreshToken={detailToken}
            onManage={openCase}
            onRecheck={openRetry}
            onNotify={() => selected && onNotifyCustomer(selected)}
            onOpenCustomer={() => selected && onOpenCustomer(selected)}
            onEscalate={openEscalate}
            onUpdateEscalation={openEscalationUpdate}
          />
        </aside>
      </div>

      <dialog className="rate-dialog" ref={dialogRef} onCancel={(event) => { event.preventDefault(); if (!saving) dialogRef.current?.close(); }}>
        <div className="dialog-header"><div><p className="eyebrow">IDENTITY OPERATION</p><h2>Recheck provider state</h2></div><button className="icon-button" onClick={() => dialogRef.current?.close()} disabled={saving} aria-label="Close"><X size={20} /></button></div>
        <div className="warning-copy"><ShieldCheck size={19} /><p>This re-reads the latest Cybrid state and synchronizes the app’s derived KYC status. It does not ask the provider to reconsider a decision.</p></div>
        <label className="field-label">Reason <span>Required</span><textarea rows={3} value={reason} onChange={(event) => setReason(event.target.value)} /></label>
        <label className="field-label confirmation-field">Type RETRY KYC<input value={confirmation} onChange={(event) => setConfirmation(event.target.value)} /></label>
        <div className="dialog-actions"><FormRequirements requirements={[{ met: reason.trim().length >= 3, label: "a reason of at least 3 characters" }, { met: confirmation === "RETRY KYC", label: "the phrase RETRY KYC" }]} /><button className="secondary-button" onClick={() => dialogRef.current?.close()} disabled={saving}>Cancel</button><button className="primary-button" disabled={reason.trim().length < 3 || confirmation !== "RETRY KYC" || saving || !canAct} onClick={() => void retry()}>{saving ? "Refreshing…" : "Authenticate & recheck"}</button></div>
      </dialog>

      <dialog className="rate-dialog kyc-case-dialog" ref={caseDialogRef} onCancel={(event) => { event.preventDefault(); if (!saving) caseDialogRef.current?.close(); }}>
        <div className="dialog-header"><div><p className="eyebrow">KYC CASE MANAGEMENT</p><h2>{(selected ? formatPersonName(selected.name) : null) ?? "Customer case"}</h2></div><button className="icon-button" onClick={() => caseDialogRef.current?.close()} disabled={saving} aria-label="Close"><X size={20} /></button></div>
        <div className="warning-copy"><ClipboardList size={19} /><p>Workflow fields and notes support operations; they do not alter the provider’s verification decision.</p></div>
        <div className="kyc-case-fields"><label className="field-label">Workflow status<select value={workflowStatus} onChange={(event) => setWorkflowStatus(event.target.value as KycCase["workflowStatus"])}><option value="unassigned">Unassigned</option><option value="open">Open</option><option value="in_review">In review</option><option value="waiting_customer">Waiting for customer</option><option value="escalated">Escalated</option><option value="resolved">Resolved</option></select></label><label className="field-label">Priority<select value={priority} onChange={(event) => setPriority(event.target.value as KycCase["priority"])}><option value="normal">Normal</option><option value="high">High</option><option value="urgent">Urgent</option></select></label></div>
        <label className="field-label">Assignee email<input type="email" value={assigneeEmail} onChange={(event) => setAssigneeEmail(event.target.value)} placeholder={viewer.email} /></label>
        <label className="field-label">Case note <span>Optional, immutable</span><textarea rows={3} value={caseNote} onChange={(event) => setCaseNote(event.target.value)} maxLength={1000} /></label>
        <label className="field-label">Reason <span>Required for audit</span><textarea rows={2} value={caseReason} onChange={(event) => setCaseReason(event.target.value)} /></label>
        <label className="field-label confirmation-field">Type UPDATE KYC CASE<input value={caseConfirmation} onChange={(event) => setCaseConfirmation(event.target.value)} /></label>
        <div className="dialog-actions"><FormRequirements requirements={[{ met: caseReason.trim().length >= 3, label: "a reason of at least 3 characters" }, { met: caseConfirmation === "UPDATE KYC CASE", label: "the phrase UPDATE KYC CASE" }]} /><button className="secondary-button" onClick={() => caseDialogRef.current?.close()} disabled={saving}>Cancel</button><button className="primary-button" disabled={caseReason.trim().length < 3 || caseConfirmation !== "UPDATE KYC CASE" || saving} onClick={() => void saveCase()}>{saving ? "Saving…" : "Authenticate & save case"}</button></div>
      </dialog>

      <dialog className="rate-dialog kyc-case-dialog" ref={escalateDialogRef} onCancel={(event) => { event.preventDefault(); if (!saving) escalateDialogRef.current?.close(); }}>
        <div className="dialog-header"><div><p className="eyebrow">PROVIDER ESCALATION</p><h2>{(selected ? formatPersonName(selected.name) : null) ?? "Customer case"}</h2></div><button className="icon-button" onClick={() => escalateDialogRef.current?.close()} disabled={saving} aria-label="Close"><X size={20} /></button></div>
        <div className="warning-copy"><LifeBuoy size={19} /><p>This files a compliance case with the banking provider. Their SLA is two calendar days. Only the provider can reverse a decline or a freeze, and they work one queue across all partners — escalate the case, not its urgency.</p></div>
        <label className="field-label">What are you asking for?<select value={escalationKind} onChange={(event) => setEscalationKind(event.target.value as KycEscalationKind)}>{ESCALATION_KINDS.map((kind) => <option key={kind} value={kind}>{escalationKindInfo(kind).label}</option>)}</select></label>
        <p className="detail-panel-note subtle">{escalationKindInfo(escalationKind).likelihood}</p>
        {escalationKind === "duplicate" ? <label className="field-label">Canonical customer GUID <span>Required</span><input value={canonicalGuid} onChange={(event) => setCanonicalGuid(event.target.value)} placeholder="The GUID to keep" /></label> : null}
        <label className="field-label">Context for the provider <span>Sent verbatim — identifiers only, never identity data</span><textarea rows={4} value={escalationContext} onChange={(event) => setEscalationContext(event.target.value)} maxLength={2000} /></label>
        {escalationPreview ? <><span className="section-label">PREVIEW</span><pre className="escalation-packet">{escalationPreview.subject}{"\n\n"}{escalationPreview.body}</pre></> : null}
        <label className="field-label">Reason <span>Required for audit</span><textarea rows={2} value={escalationReason} onChange={(event) => setEscalationReason(event.target.value)} /></label>
        <label className="field-label confirmation-field">Type ESCALATE TO PROVIDER<input value={escalationConfirmation} onChange={(event) => setEscalationConfirmation(event.target.value)} /></label>
        <div className="dialog-actions"><FormRequirements requirements={[{ met: escalationReason.trim().length >= 3, label: "a reason of at least 3 characters" }, { met: escalationKind !== "duplicate" || canonicalGuid.trim().length >= 8, label: "the canonical customer GUID" }, { met: escalationConfirmation === "ESCALATE TO PROVIDER", label: "the phrase ESCALATE TO PROVIDER" }]} /><button className="secondary-button" onClick={() => escalateDialogRef.current?.close()} disabled={saving}>Cancel</button><button className="primary-button" disabled={escalationReason.trim().length < 3 || escalationConfirmation !== "ESCALATE TO PROVIDER" || (escalationKind === "duplicate" && canonicalGuid.trim().length < 8) || saving || !canAct} onClick={() => void submitEscalation()}>{saving ? "Filing…" : "Authenticate & escalate"}</button></div>
      </dialog>

      <dialog className="rate-dialog kyc-case-dialog" ref={escalationUpdateDialogRef} onCancel={(event) => { event.preventDefault(); if (!saving) escalationUpdateDialogRef.current?.close(); }}>
        <div className="dialog-header"><div><p className="eyebrow">PROVIDER ESCALATION</p><h2>Record ticket or outcome</h2></div><button className="icon-button" onClick={() => escalationUpdateDialogRef.current?.close()} disabled={saving} aria-label="Close"><X size={20} /></button></div>
        <div className="warning-copy"><LifeBuoy size={19} /><p>The SLA clock only starts once a provider ticket reference is recorded here. Closing an escalation records what the provider decided; it does not change the customer's verification state.</p></div>
        <label className="field-label">Status<select value={escalationStatus} onChange={(event) => setEscalationStatus(event.target.value as KycEscalation["status"])}><option value="awaiting_reference">Awaiting ticket reference</option><option value="open">Open with provider</option><option value="resolved">Resolved by provider</option><option value="declined">Declined by provider</option></select></label>
        <label className="field-label">Ticket reference <span>{escalationStatus === "awaiting_reference" ? "Optional until filed" : "Required"}</span><input value={escalationReference} onChange={(event) => setEscalationReference(event.target.value)} maxLength={120} /></label>
        <label className="field-label">Ticket URL <span>Optional</span><input value={escalationReferenceUrl} onChange={(event) => setEscalationReferenceUrl(event.target.value)} placeholder="https://" /></label>
        <label className="field-label">Provider decision <span>{escalationStatus === "resolved" || escalationStatus === "declined" ? "Required" : "Optional"}</span><textarea rows={3} value={escalationOutcome} onChange={(event) => setEscalationOutcome(event.target.value)} maxLength={1000} /></label>
        <label className="field-label">Reason <span>Required for audit</span><textarea rows={2} value={escalationUpdateReason} onChange={(event) => setEscalationUpdateReason(event.target.value)} /></label>
        <label className="field-label confirmation-field">Type UPDATE ESCALATION<input value={escalationUpdateConfirmation} onChange={(event) => setEscalationUpdateConfirmation(event.target.value)} /></label>
        <div className="dialog-actions"><FormRequirements requirements={[{ met: escalationUpdateReason.trim().length >= 3, label: "a reason of at least 3 characters" }, { met: escalationStatus === "awaiting_reference" || escalationReference.trim().length > 0, label: "the provider ticket reference" }, { met: !(escalationStatus === "resolved" || escalationStatus === "declined") || escalationOutcome.trim().length >= 3, label: "what the provider decided" }, { met: escalationUpdateConfirmation === "UPDATE ESCALATION", label: "the phrase UPDATE ESCALATION" }]} /><button className="secondary-button" onClick={() => escalationUpdateDialogRef.current?.close()} disabled={saving}>Cancel</button><button className="primary-button" disabled={escalationUpdateReason.trim().length < 3 || escalationUpdateConfirmation !== "UPDATE ESCALATION" || (escalationStatus !== "awaiting_reference" && !escalationReference.trim()) || ((escalationStatus === "resolved" || escalationStatus === "declined") && escalationOutcome.trim().length < 3) || saving || !canAct} onClick={() => void submitEscalationUpdate()}>{saving ? "Saving…" : "Authenticate & save"}</button></div>
      </dialog>
    </div>
  );
}
